import React, { useState, useMemo } from "react";
import AppText from "../../../UI/Texts/AppTexts";
import { AppColumn, AppRow } from "../../../UI/Layouts";
import useTheme from "../../../../hooks/useTheme";
import { AppLinkedText, CloseCircleButton } from "../../../UI/CustomButtons";
import { SizedBox } from "../../../UI/Sizes";
import BankIDVerificationSection from "./BankIDVerificationSection";
import { t } from "i18next";
import { showToast } from "../../../../redux/Snackbar/SnackbarActions";
import { useNavigate } from "react-router-dom";
import { validateEmail } from "../../../../utils/FieldValidation";
import axios from "axios";
import { useResponsive } from "../../../UI/ResponsiveContext";
import { EMAIL_REGISTERED } from "../../../../utils/Controller/URLConstants";
import {
  LOGIN_PATH,
} from "../../../../utils/Constants/Paths";
import {
  launchOpenIDLoginURL,
  launchOpenIDRegistrationURL,
} from "../../../../utils/criiptoFunctions";
import { useDispatch, useSelector } from "react-redux";
import AppPrimaryTextfield from "../../../UI/TextInput";
import {
  getLocale,
  getAllSwedentLocationTranslation,
} from "../../../../utils/helperFunctions";
import { LightHorizontalDivider } from "../../../UI/CustomElements";
import InlineMultiSelector from "../Components/InlineMultiSelector";
import { MODAL_LOGIN } from "../../Login/LoginModal";
import { openModal } from "../../../../redux/Modals/ModalActions";


const UnifiedRegistrationFormV2 = ({
  targetPath,
  closeModal,
  asModal = false,
}) => {
  const { isMobile } = useResponsive();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const [formData, setFormData] = useState({
    email: "",
    professions: [],
    workfields: [],
    locations: [],
    employmentTypes: ['Konsult'],
  });

  const [errors, setErrors] = useState({
    email: "",
    professions: "",
    workfields: "",
    locations: "",
    employmentTypes: "",
    global: null,
  });

  const [isLoading, setLoading] = useState(false);
  const [checkingUserExists, setCheckingUserExists] = useState(false);

  const updateFormData = (key, value) => {
    setFormData((prevData) => ({ ...prevData, [key]: value }));
  };

  const core = useSelector((state) => state.core);
  const coreConstants = Object.freeze(core.constants);

  const professionOptions = useMemo(() => {
    return (coreConstants.workroles ?? []).map((role) => {
      const text = role.text[getLocale()] ?? role.text["sv"];
      return {
        key: role.id,
        text: text?.length > 0 ? text : role.id,
        value: role.id,
        suggested: role.suggested,
      };
    });
  }, [coreConstants]);

  const workfieldOptions = useMemo(() => {
    return (coreConstants.workfields ?? [])
      .filter((e) => !e.isGroup)
      .map((workfield) => ({
        key: workfield.id,
        text: workfield.text[getLocale()],
        value: workfield.id,
        suggested: workfield?.suggestedForAdmin ?? false,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [coreConstants]);

  const locationOptions = useMemo(() => {
    return (coreConstants.appLocations ?? [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((location) => ({
        key: location.key,
        value: location.key,
        text:
          location.key === "all_sweden"
            ? getAllSwedentLocationTranslation()
            : location.name,
      }));
  }, [coreConstants]);

  const handleMultiSelect = (key) => (value) => {
    if (!formData[key].includes(value)) {
      updateFormData(key, [...formData[key], value]);
    }
  };

  const handleRemoveItem = (key) => (item) => {
    updateFormData(
      key,
      formData[key].filter((i) => i !== item)
    );
  };

  const validateAndSetErrors = () => {
    const newErrors = {};

    if (!validateEmail(formData.email)) {
      newErrors.email = t("registration.contact_information.error");
    }
    if (!formData.professions.length) {
      newErrors.professions = t("registration.select_profession.error");
    }
    if (!formData.workfields.length) {
      newErrors.workfields = t("registration.select_workfield.error");
    }
    if (!formData.locations.length) {
      newErrors.locations = t("registration.select_location.error");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkIfUserExists = async () => {
    setCheckingUserExists(true);
    try {
      const response = await axios.get(
        `${EMAIL_REGISTERED}?email=${formData.email}`
      );
      if (response.status === 200 && response.data.status === 0) {
        showToast(t("already_have_account.title", { appName: theme.appName }), {
          type: "success",
          duration: 1500,
        });
        setTimeout(() => {
          setCheckingUserExists(false);
          if (isMobile) {
            navigate(LOGIN_PATH);
          } else {
            launchOpenIDLoginURL({ sameDevice: false, targetPath });
          }
        }, 1800);
        return true;
      }
    } catch (e) {
      setErrors((prev) => ({
        ...prev,
        global: "Could not check Authentication",
      }));
      return true;
    }
    setCheckingUserExists(false);
    return false;
  };

  const onCreateAccountClick = async (sameDevice = false) => {
    if (!validateAndSetErrors()) return;
    let userExists = await checkIfUserExists();
    if (!userExists) {
      setLoading(true);
      await launchOpenIDRegistrationURL(
        formData.email,
        formData,
        sameDevice,
        targetPath
      );
      setLoading(false);
    }
  };



  return (
    <AppColumn
      style={{
        height: '100vh',
        overflow: 'hidden'
      }}
    >
      <SizedBox height={theme.spacing.elementPadding} />
      <AppRow
        crossAxisAlignment="center"
        mainAxisAlignment="space-between"
        style={{
          width: "100%",
          paddingLeft: theme.spacing.elementPadding,
          paddingRight: theme.spacing.contentElementPadding,
        }}
      >


        <div style={{ position: "relative", width: "100%", display: "flex", alignItems: "center" }}>
          <AppRow crossAxisAlignment={'center'}>
            <div>
              <CloseCircleButton onClick={() => asModal && dispatch(closeModal())} />
            </div>
            <SizedBox width={theme.spacing.elementPadding} />

          </AppRow>
        </div>

      </AppRow>
      <SizedBox height={theme.spacing.elementPadding} />
      <LightHorizontalDivider />
      <div style={{
        flex: 1,
        overflowY: 'auto',
        scrollbarColor: `${theme.colors.lightGrey} transparent`,
        scrollbarGutter: 'stable',
      }}>
        <AppColumn
          style={{
            paddingLeft: theme.spacing.contentElementPadding,
            paddingRight: theme.spacing.contentElementPadding,
            paddingTop:  theme.spacing.contentElementPadding,

          }}
        >
          <AppColumn crossAxisAlignment={'start'}>
            <AppText.SubHeading
              use={"h1"}
              fontWeight={500}>
              {t("login.title", { appName: theme.appName })}
            </AppText.SubHeading>

            <SizedBox height={theme.spacing.elementPaddingSmall} />

            <AppText.Footnote
              use={"h2"}
              margins={{
                marginBottom: "0px",
                marginTop: "0px",
              }}
              center
            >
              {t('registration.v2.sub_title')}
            </AppText.Footnote>
          </AppColumn>

          <SizedBox height={isMobile ? theme.spacing.contentElementPadding : theme.spacing.contentPadding} />



          <AppPrimaryTextfield
            value={formData.email}
            keyboardTextCapitalization="none"
            prefixIcon="mail"
            error={errors.email}
            loading={checkingUserExists}
            onChange={(value) => updateFormData("email", value)}
            hintText="Enter your email e.g ''anna@gmail.com''"
            onSubmit={checkIfUserExists}
            maxLines={1}

            autoComplete="email"
          />

          <SizedBox height={theme.spacing.contentElementPadding} />

          <BankIDVerificationSection
            onCreateAccount={(sameDevice) => onCreateAccountClick(sameDevice)}
            showTitle={false}
            isLoading={isLoading || checkingUserExists}
            onSkip={() => console.log("Skipping BankID verification")}
            errors={errors}
          />
        </AppColumn>
      </div>
    </AppColumn>
  );
};

export default UnifiedRegistrationFormV2;


